import { colorStrings, mediaQueriesBP } from "@iewc/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    activeStep = "activeStep",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.activeStep]: string;
    };
}

export const mapStateToProps = (_: ApplicationState, __: OwnProps) => {
    return {};
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CheckoutFlowIndicatorStyles {
    wrapper?: InjectableCss;
    step?: InjectableCss;
    checkIcon?: IconProps;
}

export const checkoutFlowIndicatorStyles: CheckoutFlowIndicatorStyles = {
    wrapper: {
        css: css`
            display: none;

            ${mediaQueriesBP.minMD} {

            display: flex;
            justify-content: space-between;
            padding: 20px 0;
            margin-bottom: 15px;
            overflow: hidden;}
        `,
    },
    step: {
        css: css`
            width: 33.3333%;
            color: ${getColor(colorStrings.secondaryMain)};
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: 600;
            padding-block: 9px;
            max-height: 40px;
            position: relative;
            border: 1px solid ${getColor(colorStrings.commonBorder)};
            background: ${getColor(colorStrings.commonGlobalWhite)};

            .custom-icon {
                display: none;
            }

            &::before {
                content: "";
                position: absolute;
                right: -12px;
                width: 0;
                height: 0;
                border-top: 20px solid transparent;
                border-bottom: 20px solid transparent;
                border-left: 12px solid #ccc;
                z-index: 100;
                margin: -1px;
            }

            &::after {
                content: "";
                position: absolute;
                right: -10px;
                width: 0;
                height: 0;
                border-top: 20px solid transparent;
                border-bottom: 20px solid transparent;
                border-left: 12px solid #fff;
                z-index: 100;
                margin: -1px;
            }

            &.active {
                background: ${getColor(colorStrings.secondaryMain)};
                color: ${getColor(colorStrings.commonGlobalWhite)};
                border: 1px solid ${getColor(colorStrings.secondaryMain)};

                &::before {
                    display: none;
                }

                &::after {
                    right: -12px;
                    border-left: 12px solid ${getColor(colorStrings.secondaryMain)};
                }
            }

            &.completed {
                border: 1px solid #3fb13f;
                color: #3fb13f;
                background: ${getColor(colorStrings.commonGlobalWhite)};

                .custom-icon {
                    display: flex;

                    svg {
                        color: #3fb13f;
                    }
                }

                &::before {
                    right: -12px;
                    border-left: 12px solid #3fb13f;
                }

                &::after {
                    right: -10px;
                    border-left: 12px solid ${getColor(colorStrings.commonGlobalWhite)};
                }
            }

            &:last-child {
                &::before {
                    display: none;
                }

                &::after {
                    display: none;
                }
            }
        `,
    },
    checkIcon: {
        size: 20,
        css: css`
            margin-right: 5px;
            border-radius: 50%;
            border: 1px solid #3fb13f;
            padding: 2px;
        `,
    },
};

export const styles = checkoutFlowIndicatorStyles;

export const CheckoutFlowIndicatorWidget = ({ fields: { activeStep } }: Props) => {
    const steps = [
        { step: "billingShipping", label: "Billing & Shipping" },
        { step: "payment", label: "Payment" },
        { step: "confirmation", label: "Confirmation" },
    ];

    return (
        <StyledWrapper {...styles.wrapper}>
            {steps.map((step, index) => {
                const isCompleted = index < steps.findIndex(s => s.step === activeStep);
                const isActive = step.step === activeStep;

                return (
                    <StyledWrapper
                        {...styles.step}
                        key={step.step}
                        className={`${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                    >
                        <Icon {...styles.checkIcon} className="custom-icon" src={Check} />
                        {translate(step.label)}
                    </StyledWrapper>
                );
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutFlowIndicatorWidget) as any,
    definition: {
        displayName: "Checkout Flow Indicator",
        group: "Verndale Box" as any,
        icon: "check",
        fieldDefinitions: [
            {
                name: fields.activeStep,
                displayName: "Active Step",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Billing & Shipping",
                        value: "billingShipping",
                    },
                    {
                        displayName: "Payment",
                        value: "payment",
                    },
                    {
                        displayName: "Confirmation",
                        value: "confirmation",
                    },
                ],
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
